const API_BASE =
  (import.meta.env.VITE_APP_API_BASE_URL as string | undefined) ||
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
  '';

export class ApiError extends Error {
  code: string;
  status: number;

  constructor(message: string, code = 'API_ERROR', status = 0) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

type ApiEnvelope<T> = {
  success?: boolean;
  data?: T;
  error?: {
    code?: string;
    message?: string;
  };
  detail?:
    | string
    | {
        code?: string;
        message?: string;
      }
    | Array<{
        type?: string;
        msg?: string;
        message?: string;
      }>;
};

function resolveApiError<T>(
  payload: ApiEnvelope<T>,
  status: number,
): { code: string; message: string } {
  if (payload.error) {
    return {
      code: payload.error.code || 'API_ERROR',
      message: payload.error.message || `请求失败 (${status})`,
    };
  }

  const { detail } = payload;
  if (typeof detail === 'string') {
    return { code: 'API_ERROR', message: detail };
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    return {
      code: first.type || 'API_ERROR',
      message: first.message || first.msg || `请求失败 (${status})`,
    };
  }
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    return {
      code: detail.code || 'API_ERROR',
      message: detail.message || `请求失败 (${status})`,
    };
  }

  return { code: 'API_ERROR', message: `请求失败 (${status})` };
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
  accessToken?: string | null,
): Promise<T> {
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  const payload = (await response.json().catch(() => ({}))) as ApiEnvelope<T>;
  if (!response.ok || payload.success === false) {
    const error = resolveApiError(payload, response.status);
    throw new ApiError(error.message, error.code, response.status);
  }

  return (payload.data ?? payload) as T;
}

export function getDeviceId(): string {
  const key = 'fm_device_id';
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `web-${Date.now()}`;
  localStorage.setItem(key, id);
  return id;
}
